import {Dropdown} from '@/components/atoms'
import {ButtonIcon, TextInput} from 'pepsico-ds'
import PropTypes from 'prop-types'
import {useEffect, useMemo, useState} from 'react'

export const TablePagination = ({
  totalPages = 0,
  page,
  handlePageChange,
  handleRowsPerPageChange = () => {},
  enableRowsPerPage = false,
  rowsPerPage = 10,
  rowsPerPageOptions = [10, 25, 50, 100],
  totalRecords = 0,
  showTotalRecords = false,
  isLoading = false,
}) => {
  const [pageInput, setPageInput] = useState(page.toString())

  const handlePageInputChange = value => {
    setPageInput(value)
  }

  const handlePageInputSubmit = event => {
    if (event.key === 'Enter') {
      const newDisplayPage = parseInt(pageInput, 10)
      if (newDisplayPage >= 1 && newDisplayPage <= totalPages) {
        handlePageChange(newDisplayPage)
      } else {
        setPageInput(page.toString())
      }
    }
  }

  const handlePageInputBlur = () => {
    const newDisplayPage = parseInt(pageInput, 10)
    if (newDisplayPage >= 1 && newDisplayPage <= totalPages) {
      handlePageChange(newDisplayPage)
    } else {
      setPageInput(page.toString())
    }
  }

  useEffect(() => {
    setPageInput(page.toString())
  }, [page])

  const rowsPerPageValues = useMemo(() => {
    return rowsPerPageOptions.map(option => ({
      id: option,
      displayText: option,
    }))
  }, [rowsPerPageOptions])

  return (
    <div className="flex flex-col">
      <div className="flex w-full items-center justify-between">
        {enableRowsPerPage && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-50">Rows per page</span>
            <Dropdown
              value={[{id: rowsPerPage, displayText: rowsPerPage}]}
              updateValue={handleRowsPerPageChange}
              schema={{
                options: rowsPerPageValues,
              }}
              uiSchema={{
                isDark: true,
                selection: 'single',
                size: 'medium',
                className: 'max-w-20',
              }}
              required={false}
              isJsonWrap={true}
              autocomplete="off"
              isClearable={false}
              menuPosition="top-right"
            />
          </div>
        )}

        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <ButtonIcon
              icon="first_page"
              onClick={() => handlePageChange(1)}
              disabled={page === 1 || isLoading}
              type="naked"
            />
            <ButtonIcon
              icon="keyboard_arrow_left"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1 || isLoading}
              type="naked"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-50">Page</span>
            <TextInput
              value={pageInput}
              onUpdate={handlePageInputChange}
              onKeyDown={handlePageInputSubmit}
              onBlur={handlePageInputBlur}
              isClearable={false}
              type="number"
              className="max-w-20 [&_.right-action-container]:!hidden"
              size="small"
              disabled={isLoading}
            />
            <span className="text-sm font-medium text-gray-50">of {totalPages}</span>
          </div>

          <div className="flex items-center gap-2">
            <ButtonIcon
              icon="keyboard_arrow_right"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages || isLoading}
              type="naked"
            />
            <ButtonIcon
              icon="last_page"
              onClick={() => handlePageChange(totalPages)}
              disabled={page >= totalPages || isLoading}
              type="naked"
            />
          </div>
        </div>
      </div>

      {showTotalRecords && <p className="pt-4 text-end text-sm">{totalRecords} total items </p>}
    </div>
  )
}

TablePagination.propTypes = {
  totalPages: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleRowsPerPageChange: PropTypes.func.isRequired,
  enableRowsPerPage: PropTypes.bool,
  rowsPerPage: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  isLoading: PropTypes.bool,
}
